import { useState, useEffect } from 'react';
import { Save, Eye, EyeOff, ChevronDown, ChevronUp, RefreshCw, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import adminApi from '@/lib/adminApi';

const sectionLabels = {
  hero: 'Section Hero / Accueil',
  about: 'Qui sommes-nous',
  missions: 'Nos missions',
  ecosystem: 'Écosystème régional',
  network: 'Réseau national',
  figures: 'Chiffres clés',
  contact: 'Contact',
  footer: 'Pied de page',
  legal: 'Mentions légales',
  privacy: 'Politique de confidentialité'
};

// Configuration des champs extra par section
const extraFieldsConfig = {
  hero: [
    { key: 'badge_text', label: 'Badge (texte au-dessus du titre)', type: 'text' },
    { key: 'cta_primary', label: 'Bouton principal (CTA)', type: 'text' },
    { key: 'cta_secondary', label: 'Bouton secondaire', type: 'text' },
    { key: 'year_badge', label: 'Année (badge)', type: 'text' },
    { key: 'year_label', label: 'Label année', type: 'text' }
  ],
  about: [
    { key: 'badge_text', label: 'Badge de section', type: 'text' },
    { key: 'governance_title', label: 'Titre gouvernance', type: 'text' },
    { key: 'network_badge_line1', label: 'Badge réseau ligne 1', type: 'text' },
    { key: 'network_badge_line2', label: 'Badge réseau ligne 2', type: 'text' },
    { key: 'network_badge_line3', label: 'Badge réseau ligne 3', type: 'text' }
  ],
  network: [
    { key: 'badge_text', label: 'Badge de section', type: 'text' },
    { key: 'feature1_title', label: 'Caractéristique 1 - Titre', type: 'text' },
    { key: 'feature1_desc', label: 'Caractéristique 1 - Description', type: 'text' },
    { key: 'feature2_title', label: 'Caractéristique 2 - Titre', type: 'text' },
    { key: 'feature2_desc', label: 'Caractéristique 2 - Description', type: 'text' },
    { key: 'card_title_line1', label: 'Carte centrale - Ligne 1', type: 'text' },
    { key: 'card_title_line2', label: 'Carte centrale - Ligne 2', type: 'text' },
    { key: 'card_description', label: 'Carte centrale - Description', type: 'textarea' }
  ],
  missions: [
    { key: 'badge_text', label: 'Badge de section', type: 'text' },
    { key: 'values_title', label: 'Bloc valeurs - Titre', type: 'text' },
    { key: 'values_content', label: 'Bloc valeurs - Contenu', type: 'textarea' },
    { key: 'values_quote', label: 'Bloc valeurs - Citation', type: 'textarea' }
  ],
  ecosystem: [
    { key: 'badge_text', label: 'Badge de section', type: 'text' },
    { key: 'founders_title', label: 'Titre membres fondateurs', type: 'text' },
    { key: 'active_title', label: 'Titre membres actifs', type: 'text' },
    { key: 'cta_title', label: 'CTA - Titre', type: 'text' },
    { key: 'cta_description', label: 'CTA - Description', type: 'textarea' },
    { key: 'cta_button', label: 'CTA - Texte bouton', type: 'text' }
  ],
  figures: [
    { key: 'badge_text', label: 'Badge de section', type: 'text' },
    { key: 'bottom_text', label: 'Texte en bas', type: 'text' }
  ],
  contact: [
    { key: 'badge_text', label: 'Badge de section', type: 'text' },
    { key: 'info_title', label: 'Titre coordonnées', type: 'text' },
    { key: 'address_label', label: 'Label adresse', type: 'text' },
    { key: 'phone_label', label: 'Label téléphone', type: 'text' },
    { key: 'email_label', label: 'Label email', type: 'text' },
    { key: 'quote', label: 'Citation', type: 'text' },
    { key: 'form_nom', label: 'Formulaire - Nom', type: 'text' },
    { key: 'form_prenom', label: 'Formulaire - Prénom', type: 'text' },
    { key: 'form_email', label: 'Formulaire - Email', type: 'text' },
    { key: 'form_telephone', label: 'Formulaire - Téléphone', type: 'text' },
    { key: 'form_organisation', label: 'Formulaire - Organisation', type: 'text' },
    { key: 'form_objet', label: 'Formulaire - Objet', type: 'text' },
    { key: 'form_message', label: 'Formulaire - Message', type: 'text' },
    { key: 'form_rgpd', label: 'Formulaire - Texte RGPD', type: 'textarea' },
    { key: 'form_submit', label: 'Formulaire - Bouton envoi', type: 'text' }
  ],
  footer: [
    { key: 'network_text', label: 'Texte réseau', type: 'text' },
    { key: 'nav_title', label: 'Titre navigation', type: 'text' },
    { key: 'contact_title', label: 'Titre contact', type: 'text' },
    { key: 'copyright', label: 'Copyright', type: 'text' },
    { key: 'admin_link', label: 'Lien admin', type: 'text' },
    { key: 'legal_link', label: 'Lien mentions légales', type: 'text' },
    { key: 'privacy_link', label: 'Lien confidentialité', type: 'text' }
  ],
  legal: [
    { key: 'section1_title', label: 'Section 1 - Titre (Éditeur)', type: 'text' },
    { key: 'section1_content', label: 'Section 1 - Contenu', type: 'textarea' },
    { key: 'section2_title', label: 'Section 2 - Titre (Hébergement)', type: 'text' },
    { key: 'section2_content', label: 'Section 2 - Contenu', type: 'textarea' },
    { key: 'section3_title', label: 'Section 3 - Titre (Propriété intellectuelle)', type: 'text' },
    { key: 'section3_content', label: 'Section 3 - Contenu', type: 'textarea' },
    { key: 'section4_title', label: 'Section 4 - Titre (Responsabilité)', type: 'text' },
    { key: 'section4_content', label: 'Section 4 - Contenu', type: 'textarea' },
    { key: 'section5_title', label: 'Section 5 - Titre (Liens hypertextes)', type: 'text' },
    { key: 'section5_content', label: 'Section 5 - Contenu', type: 'textarea' },
    { key: 'section6_title', label: 'Section 6 - Titre (Données personnelles)', type: 'text' },
    { key: 'section6_content', label: 'Section 6 - Contenu', type: 'textarea' },
    { key: 'section7_title', label: 'Section 7 - Titre (Cookies)', type: 'text' },
    { key: 'section7_content', label: 'Section 7 - Contenu', type: 'textarea' },
    { key: 'section8_title', label: 'Section 8 - Titre (Droit applicable)', type: 'text' },
    { key: 'section8_content', label: 'Section 8 - Contenu', type: 'textarea' }
  ],
  privacy: [
    { key: 'section1_title', label: 'Section 1 - Titre (Introduction)', type: 'text' },
    { key: 'section1_content', label: 'Section 1 - Contenu', type: 'textarea' },
    { key: 'section2_title', label: 'Section 2 - Titre (Responsable)', type: 'text' },
    { key: 'section2_content', label: 'Section 2 - Contenu', type: 'textarea' },
    { key: 'section3_title', label: 'Section 3 - Titre (Données collectées)', type: 'text' },
    { key: 'section3_content', label: 'Section 3 - Contenu', type: 'textarea' },
    { key: 'section4_title', label: 'Section 4 - Titre (Finalités)', type: 'text' },
    { key: 'section4_content', label: 'Section 4 - Contenu', type: 'textarea' },
    { key: 'section5_title', label: 'Section 5 - Titre (Base légale)', type: 'text' },
    { key: 'section5_content', label: 'Section 5 - Contenu', type: 'textarea' },
    { key: 'section6_title', label: 'Section 6 - Titre (Destinataires)', type: 'text' },
    { key: 'section6_content', label: 'Section 6 - Contenu', type: 'textarea' },
    { key: 'section7_title', label: 'Section 7 - Titre (Durée conservation)', type: 'text' },
    { key: 'section7_content', label: 'Section 7 - Contenu', type: 'textarea' },
    { key: 'section8_title', label: 'Section 8 - Titre (Sécurité)', type: 'text' },
    { key: 'section8_content', label: 'Section 8 - Contenu', type: 'textarea' },
    { key: 'section9_title', label: 'Section 9 - Titre (Droits utilisateurs)', type: 'text' },
    { key: 'section9_content', label: 'Section 9 - Contenu', type: 'textarea' },
    { key: 'section10_title', label: 'Section 10 - Titre (CNIL)', type: 'text' },
    { key: 'section10_content', label: 'Section 10 - Contenu', type: 'textarea' },
    { key: 'section11_title', label: 'Section 11 - Titre (Cookies)', type: 'text' },
    { key: 'section11_content', label: 'Section 11 - Contenu', type: 'textarea' },
    { key: 'section12_title', label: 'Section 12 - Titre (Modification)', type: 'text' },
    { key: 'section12_content', label: 'Section 12 - Contenu', type: 'textarea' }
  ]
};

const ContentEditor = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [expanded, setExpanded] = useState({});

  const fetchContent = async () => {
    try {
      const response = await adminApi.get(`/content`);
      setSections(response.data);
      // Expand first section by default
      if (response.data.length > 0) {
        setExpanded({ [response.data[0].section_key]: true });
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Erreur lors du chargement des contenus');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleChange = (sectionKey, field, value) => {
    setSections(prev =>
      prev.map(section =>
        section.section_key === sectionKey
          ? { ...section, [field]: value }
          : section
      )
    );
  };

  const handleExtraFieldChange = (sectionKey, fieldKey, value) => {
    setSections(prev =>
      prev.map(section => {
        if (section.section_key === sectionKey) {
          return {
            ...section,
            extra_fields: {
              ...(section.extra_fields || {}),
              [fieldKey]: value
            }
          };
        }
        return section;
      })
    );
  };

  const handleItemChange = (sectionKey, itemIndex, field, value) => {
    setSections(prev =>
      prev.map(section => {
        if (section.section_key === sectionKey && section.items) {
          const newItems = [...section.items];
          newItems[itemIndex] = { ...newItems[itemIndex], [field]: value };
          return { ...section, items: newItems };
        }
        return section;
      })
    );
  };

  const addItem = (sectionKey, template) => {
    setSections(prev =>
      prev.map(section => {
        if (section.section_key === sectionKey) {
          return {
            ...section,
            items: [...(section.items || []), template]
          };
        }
        return section;
      })
    );
  };

  const removeItem = (sectionKey, itemIndex) => {
    setSections(prev =>
      prev.map(section => {
        if (section.section_key === sectionKey && section.items) {
          const newItems = section.items.filter((_, i) => i !== itemIndex);
          return { ...section, items: newItems };
        }
        return section;
      })
    );
  };

  const handleSave = async (sectionKey) => {
    const section = sections.find(s => s.section_key === sectionKey);
    if (!section) return;

    setSaving(prev => ({ ...prev, [sectionKey]: true }));
    try {
      await adminApi.put(`/content/${sectionKey}`, {
        title: section.title,
        subtitle: section.subtitle,
        content: section.content,
        items: section.items,
        extra_fields: section.extra_fields,
        is_visible: section.is_visible
      });
      toast.success(`Section "${sectionLabels[sectionKey]}" enregistrée`);
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(prev => ({ ...prev, [sectionKey]: false }));
    }
  };

  const toggleExpanded = (sectionKey) => {
    setExpanded(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const renderItemsEditor = (section) => {
    if (!section.items || section.items.length === 0) return null;

    const sectionKey = section.section_key;
    
    // Différents templates selon la section
    if (sectionKey === 'about') {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="font-inter font-medium">Gouvernance (personnes)</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => addItem(sectionKey, { label: '', value: '', description: '' })}
            >
              <Plus size={16} className="mr-1" /> Ajouter
            </Button>
          </div>
          {section.items.map((item, idx) => (
            <div key={idx} className="border rounded p-4 space-y-3 bg-slate-50">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-slate-500">Personne {idx + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => removeItem(sectionKey, idx)} className="text-red-500 h-8 w-8 p-0">
                  <Trash2 size={16} />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input
                  placeholder="Fonction"
                  value={item.label || ''}
                  onChange={(e) => handleItemChange(sectionKey, idx, 'label', e.target.value)}
                />
                <Input
                  placeholder="Nom"
                  value={item.value || ''}
                  onChange={(e) => handleItemChange(sectionKey, idx, 'value', e.target.value)}
                />
                <Input
                  placeholder="Description"
                  value={item.description || ''}
                  onChange={(e) => handleItemChange(sectionKey, idx, 'description', e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (sectionKey === 'missions') {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="font-inter font-medium">Missions</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => addItem(sectionKey, { icon: 'Users', title: '', description: '' })}
            >
              <Plus size={16} className="mr-1" /> Ajouter
            </Button>
          </div>
          {section.items.map((item, idx) => (
            <div key={idx} className="border rounded p-4 space-y-3 bg-slate-50">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-slate-500">Mission {idx + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => removeItem(sectionKey, idx)} className="text-red-500 h-8 w-8 p-0">
                  <Trash2 size={16} />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <select
                  className="border rounded px-3 py-2 text-sm"
                  value={item.icon || 'Users'}
                  onChange={(e) => handleItemChange(sectionKey, idx, 'icon', e.target.value)}
                >
                  <option value="Users">Users</option>
                  <option value="TrendingUp">TrendingUp</option>
                  <option value="Lightbulb">Lightbulb</option>
                  <option value="Target">Target</option>
                  <option value="Shield">Shield</option>
                  <option value="Handshake">Handshake</option>
                </select>
                <Input
                  placeholder="Titre"
                  value={item.title || ''}
                  onChange={(e) => handleItemChange(sectionKey, idx, 'title', e.target.value)}
                />
                <Input
                  placeholder="Description"
                  value={item.description || ''}
                  onChange={(e) => handleItemChange(sectionKey, idx, 'description', e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (sectionKey === 'ecosystem') {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="font-inter font-medium">Catégories de membres</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => addItem(sectionKey, { category: '', examples: [] })}
            >
              <Plus size={16} className="mr-1" /> Ajouter
            </Button>
          </div>
          {section.items.map((item, idx) => (
            <div key={idx} className="border rounded p-4 space-y-3 bg-slate-50">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-slate-500">Catégorie {idx + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => removeItem(sectionKey, idx)} className="text-red-500 h-8 w-8 p-0">
                  <Trash2 size={16} />
                </Button>
              </div>
              <Input
                placeholder="Nom de la catégorie"
                value={item.category || ''}
                onChange={(e) => handleItemChange(sectionKey, idx, 'category', e.target.value)}
              />
              <Input
                placeholder="Exemples (séparés par des virgules)"
                value={(item.examples || []).join(', ')}
                onChange={(e) => handleItemChange(sectionKey, idx, 'examples', e.target.value.split(',').map(s => s.trim()))}
              />
            </div>
          ))}
        </div>
      );
    }

    if (sectionKey === 'figures') {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="font-inter font-medium">Chiffres clés</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => addItem(sectionKey, { value: '', label: '', description: '' })}
            >
              <Plus size={16} className="mr-1" /> Ajouter
            </Button>
          </div>
          {section.items.map((item, idx) => (
            <div key={idx} className="border rounded p-4 space-y-3 bg-slate-50">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-slate-500">Chiffre {idx + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => removeItem(sectionKey, idx)} className="text-red-500 h-8 w-8 p-0">
                  <Trash2 size={16} />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input
                  placeholder="Valeur (ex: 2017)"
                  value={item.value || ''}
                  onChange={(e) => handleItemChange(sectionKey, idx, 'value', e.target.value)}
                />
                <Input
                  placeholder="Label"
                  value={item.label || ''}
                  onChange={(e) => handleItemChange(sectionKey, idx, 'label', e.target.value)}
                />
                <Input
                  placeholder="Description"
                  value={item.description || ''}
                  onChange={(e) => handleItemChange(sectionKey, idx, 'description', e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a8a]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-editor">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-playfair text-2xl font-semibold text-[#0F172A]">
            Gestion des contenus
          </h2>
          <p className="text-slate-600 font-inter text-sm mt-1">
            Modifiez tous les textes, CTA et éléments de chaque section
          </p>
        </div>
      </div>

      {sections.map((section) => (
        <Card key={section.section_key} className="overflow-hidden" data-testid={`section-editor-${section.section_key}`}>
          <CardHeader
            className="cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => toggleExpanded(section.section_key)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <CardTitle className="font-playfair text-lg">
                  {sectionLabels[section.section_key] || section.section_key}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {section.is_visible ? (
                    <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                      <Eye size={12} /> Visible
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                      <EyeOff size={12} /> Masquée
                    </span>
                  )}
                </div>
              </div>
              {expanded[section.section_key] ? (
                <ChevronUp size={20} className="text-slate-400" />
              ) : (
                <ChevronDown size={20} className="text-slate-400" />
              )}
            </div>
          </CardHeader>

          {expanded[section.section_key] && (
            <CardContent className="space-y-6 pt-0">
              {/* Visibility toggle */}
              <div className="flex items-center justify-between py-4 border-b">
                <div>
                  <Label className="font-inter font-medium">Afficher cette section</Label>
                  <p className="text-sm text-slate-500">Active ou désactive l'affichage sur le site</p>
                </div>
                <Switch
                  checked={section.is_visible}
                  onCheckedChange={(checked) =>
                    handleChange(section.section_key, 'is_visible', checked)
                  }
                />
              </div>

              {/* Main fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-inter text-sm font-medium">Titre</Label>
                  <Input
                    value={section.title || ''}
                    onChange={(e) => handleChange(section.section_key, 'title', e.target.value)}
                    className="mt-2"
                    placeholder="Titre de la section"
                  />
                </div>
                <div>
                  <Label className="font-inter text-sm font-medium">Sous-titre</Label>
                  <Input
                    value={section.subtitle || ''}
                    onChange={(e) => handleChange(section.section_key, 'subtitle', e.target.value)}
                    className="mt-2"
                    placeholder="Sous-titre"
                  />
                </div>
              </div>

              {/* Content */}
              {section.section_key !== 'figures' && (
                <div>
                  <Label className="font-inter text-sm font-medium">Contenu principal</Label>
                  <Textarea
                    value={section.content || ''}
                    onChange={(e) => handleChange(section.section_key, 'content', e.target.value)}
                    className="mt-2 min-h-[100px]"
                    placeholder="Contenu de la section"
                  />
                </div>
              )}

              {/* Extra fields */}
              {extraFieldsConfig[section.section_key] && (
                <div className="border-t pt-4">
                  <h4 className="font-inter font-medium text-slate-700 mb-4">Textes additionnels</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {extraFieldsConfig[section.section_key].map((field) => (
                      <div key={field.key}>
                        <Label className="font-inter text-sm">{field.label}</Label>
                        {field.type === 'textarea' ? (
                          <Textarea
                            value={section.extra_fields?.[field.key] || ''}
                            onChange={(e) => handleExtraFieldChange(section.section_key, field.key, e.target.value)}
                            className="mt-2"
                            placeholder={field.label}
                          />
                        ) : (
                          <Input
                            value={section.extra_fields?.[field.key] || ''}
                            onChange={(e) => handleExtraFieldChange(section.section_key, field.key, e.target.value)}
                            className="mt-2"
                            placeholder={field.label}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Items editor */}
              {section.items && section.items.length > 0 && (
                <div className="border-t pt-4">
                  {renderItemsEditor(section)}
                </div>
              )}

              {/* Save button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={() => handleSave(section.section_key)}
                  disabled={saving[section.section_key]}
                  className="bg-[#1e3a8a] hover:bg-[#172554]"
                >
                  {saving[section.section_key] ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
};

export default ContentEditor;
